import { useEffect, useState } from "react";
import { format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUS_STYLES = {
  answered: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  completed: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  door_unlocked: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  rejected: "text-red-400 bg-red-500/10 border-red-500/30",
  missed: "text-slate-400 bg-slate-500/10 border-slate-500/30",
  ringing: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
};

export function LogsTab({ compact = false }) {
  const [calls, setCalls] = useState([]);
  const [status, setStatus] = useState("all");

  const load = () => {
    const q = status === "all" ? "" : `?status=${status}`;
    api.get(`/admin/calls${q}`).then((res) => setCalls(res.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const rows = compact ? calls.slice(0, 5) : calls;

  return (
    <div data-testid="logs-tab">
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-100">Call Activity Log</h3>
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="logs-status-filter" className="w-40 bg-[#0A0D14] border-[#1E2638] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="door_unlocked">Door Unlocked</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="logs-refresh-btn" variant="outline" size="sm" onClick={load}
              className="border-[#1E2638] bg-transparent hover:bg-[#1E2638] text-slate-300">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="ops-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead>
              <tr className="text-left ops-caption border-b border-[#1E2638] bg-[#0A0D14]">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">To Ext</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500" data-testid="logs-empty">No call activity yet.</td></tr>
              )}
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-[#1E2638]/50 last:border-0 hover:bg-[#0A0D14]/60" data-testid={`log-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-300">
                    {format(new Date(c.created_at), "dd MMM HH:mm:ss")}
                  </td>
                  <td className="px-4 py-3 text-slate-200">{c.from_label}</td>
                  <td className="px-4 py-3 font-mono text-emerald-400">{c.to_extension_number}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLES[c.status] || STATUS_STYLES.missed}`}>
                      {c.status.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
